"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Session = void 0;
var session_protocol_1 = require("@augloop/session-protocol");
var types_core_1 = require("@augloop/types-core");
var annotation_activation_tracker_1 = require("./annotation-activation-tracker");
var batch_manager_1 = require("./batch-manager");
var estimate_size_1 = require("./estimate-size");
var empty_session_manager_1 = require("./websocket/empty-session-manager");
var local_workflow_execution_manager_1 = require("./local-workflow-execution-manager");
var Session = /** @class */ (function () {
    function Session(config) {
        this.annotationActivationTrackers = new Map();
        this.annotationCallbacks = new Map();
        this.annotationResultStates = new Map();
        this.tokensByAnnotationType = new Map();
        this.nextSyncSequenceId = 1;
        this.allowSeed = true;
        this.allowGroupSeed = true;
        this.seedGroupSize = 0;
        this.hasSessionConnected = false;
        this.isSessionClosed = false;
        this.isSessionConnected = false;
        this.hostCallbacks = config.hostCallbacks;
        this.sessionManager = config.sessionManager;
        this.operationBatchConfig = config.batchOptions ? this.getOperationBatchConfig(config.batchOptions) : undefined;
        this.extensionConfigs = config.extensionConfigs;
        this.clientMetadata = config.clientMetadata;
        this.modelDownloader = config.modelDownloader;
        this.sessionManager.on('sessionClose', this.onSessionClose.bind(this));
        this.sessionManager.on('reconnect', this.onReconnect.bind(this));
        this.sessionManager.on('connect', this.onConnect.bind(this));
        this.sessionManager.on('disconnect', this.onDisconnect.bind(this));
        this.sessionManager.on('resetNextSyncSequenceId', this.resetNextSyncSequenceId.bind(this));
        this.sessionManager.onMessage(session_protocol_1.AnnotationResultsMessage.getTypeName(), this.onAnnotationResults.bind(this));
        this.sessionManager.onMessage(session_protocol_1.AnnotationResultStateMessage.getTypeName(), this.onAnnotationResultStateMessage.bind(this));
    }
    Session.prototype.initialize = function () {
        var _this = this;
        return this.sessionManager.init(this.extensionConfigs, this.hostCallbacks.onInitSession ? this.hostCallbacks.onInitSession : undefined)
            .then(function () { return _this; });
    };
    Object.defineProperty(Session.prototype, "isConnected", {
        get: function () {
            return this.isSessionConnected;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "hasConnected", {
        get: function () {
            return this.hasSessionConnected;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Session.prototype, "isClosed", {
        get: function () {
            return this.isSessionClosed;
        },
        enumerable: false,
        configurable: true
    });
    Session.prototype.registerLocalWorkflow = function (workflow) {
        if (!this.localWorkflowManager) {
            this.localWorkflowManager = new local_workflow_execution_manager_1.LocalWorkflowExecutionManager(this, this.modelDownloader);
        }
        this.localWorkflowManager.registerLocalWorkflow(workflow);
    };
    Session.prototype.activateAnnotation = function (annotationType, options) {
        var _a;
        var tracker = new annotation_activation_tracker_1.AnnotationActivationTracker(this.getSessionManager.bind(this), annotationType, options);
        if (options && options.callback) {
            var map = this.annotationCallbacks.get(annotationType);
            if (!map) {
                map = new Map();
                this.annotationCallbacks.set(annotationType, map);
            }
            map.set(tracker.token, options.callback);
        }
        var tokens = (_a = this.tokensByAnnotationType.get(annotationType)) !== null && _a !== void 0 ? _a : [];
        if (tokens.indexOf(tracker.token) === -1) {
            tokens.push(tracker.token);
        }
        this.tokensByAnnotationType.set(annotationType, tokens);
        this.annotationActivationTrackers.set(tracker.token, tracker);
        tracker.activate(false /*ignoreExistingAnnotations*/);
        return tracker.asPromise();
    };
    Session.prototype.updateAnnotationConfig = function (_token, _config) {
        throw new Error('Not Yet Implemented');
    };
    Session.prototype.releaseAnnotation = function (token) {
        var tracker = this.annotationActivationTrackers.get(token);
        if (tracker) {
            this.annotationActivationTrackers.delete(token);
            var map = this.annotationCallbacks.get(tracker.annotationType);
            if (map) {
                map.delete(token);
                if (map.size == 0) {
                    this.annotationCallbacks.delete(tracker.annotationType);
                }
            }
            return tracker.release();
        }
        return Promise.resolve(false);
    };
    Session.prototype.setAnnotationState = function (parentPath, id, value) {
        var M_ = { state: value };
        this.submitOperation(new types_core_1.UpdateAnnotationMetaDataOperation({ parentPath: parentPath, items: [{ id: id }], M_: M_ }));
    };
    Session.prototype.setAnnotationMetadata = function (parentPath, id, value) {
        this.submitOperation(new types_core_1.UpdateAnnotationMetaDataOperation({ parentPath: parentPath, items: [{ id: id }], M_: value }));
    };
    Session.prototype.submitOperation = function (operation, cv) {
        this.submitOperations([operation], cv);
    };
    Session.prototype.submitOperations = function (operations, cv) {
        var _this = this;
        var isSignalOp = function (op) { return types_core_1.SchemaObject.getTypeNameFor(op) === types_core_1.SignalOperation.getTypeName(); };
        var signalOps = operations.filter(function (op) { return isSignalOp(op); });
        var modelOps = operations.filter(function (op) { return !isSignalOp(op); });
        // Signal ops are sent immediately regardless of batch config and are not sequenced
        if (signalOps.length > 0) {
            this.getSessionManager().sendMessage(new session_protocol_1.SyncMessage({
                cv: cv,
                ops: signalOps
            }));
        }
        if (modelOps.length > 0) {
            if (this.operationBatchConfig) {
                if (!this.batchedOperationsManager) {
                    var submit = function (operations, batch, callback) {
                        _this.getSessionManager().sendMessage(new session_protocol_1.SyncMessage({
                            cv: batch.cv,
                            seq: _this.nextSyncSequenceId++,
                            ops: operations
                        }), function (errorResponse) {
                            callback(errorResponse ? new Error(errorResponse.error) : undefined);
                        });
                    };
                    this.batchedOperationsManager = new batch_manager_1.BatchManager(submit);
                }
                this.batchedOperationsManager.addBatchItem(modelOps, this.operationBatchConfig, { name: 'SubmitOperations' }, cv);
            }
            else {
                this.getSessionManager().sendMessage(new session_protocol_1.SyncMessage({
                    cv: cv,
                    seq: this.nextSyncSequenceId++,
                    ops: modelOps
                }));
            }
        }
        if (this.localWorkflowManager) {
            this.localWorkflowManager.runLocalWorkflows(operations);
        }
    };
    Session.prototype.submitSeedOperations = function (operations, cv) {
        if (!this.allowSeed) {
            throw new Error('Cannot submit seed operations more than once per session');
        }
        this.allowSeed = false;
        this.allowGroupSeed = false;
        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, true, cv);
        }
        else {
            this.getSessionManager()
                .sendMessage(new session_protocol_1.SyncMessage({
                cv: cv,
                seq: 0,
                ops: operations
            }));
        }
    };
    Session.prototype.submitSeedGroupOperations = function (operations, groupComplete, cv) {
        if (!this.allowGroupSeed) {
            throw new Error('Seed operations are not allowed for this session');
        }
        if (groupComplete) {
            this.allowGroupSeed = false;
        }
        this.allowSeed = false;
        if (this.operationBatchConfig) {
            this.sendSeedMessagesViaBatchManager(operations, !!groupComplete, cv);
        }
        else {
            this.seedGroupSize++;
            this.getSessionManager()
                .sendMessage(new session_protocol_1.SyncMessage({
                cv: cv,
                seq: 0,
                ops: operations,
                groupId: 'Seed',
                groupSize: groupComplete ? this.seedGroupSize : undefined,
                groupComplete: groupComplete ? groupComplete : undefined
            }));
        }
    };
    Session.prototype.submitCustomMessage = function (message) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.getSessionManager()
                .sendMessage(message, function (err, response) {
                if (err) {
                    reject(err);
                }
                else {
                    resolve(response);
                }
            });
        });
    };
    Session.prototype.requestCacheDump = function (cacheRequestParameters) {
        var _this = this;
        if (cacheRequestParameters) {
            throw new Error('NYI');
        }
        else {
            var toReturn = new Promise(function (resolve, reject) {
                _this.getSessionManager()
                    .sendMessage(new session_protocol_1.CacheDumpRequestMessage(), (function (err, response) {
                    if (!err) {
                        resolve(response);
                    }
                    else {
                        reject(err);
                    }
                }));
            });
            return toReturn;
        }
    };
    Session.prototype.forceReconnect = function (extensionConfigs) {
        this.extensionConfigs = extensionConfigs;
        return this.getSessionManager().forceReconnect(extensionConfigs);
    };
    Session.prototype.close = function () {
        this.isSessionClosed = true;
        this.getSessionManager().closeSession();
    };
    Session.prototype.getClientMetadata = function () {
        return this.clientMetadata;
    };
    Session.prototype.setSessionCloseCallback = function (callback) {
        this.sessionCloseCallback = callback;
    };
    Session.prototype.setConnectCallback = function (callback) {
        this.connectCallback = callback;
    };
    Session.prototype.setDisconnectCallback = function (callback) {
        this.disconnectCallback = callback;
    };
    Session.prototype.setReconnectCallback = function (callback) {
        this.reconnectCallback = callback;
    };
    Session.prototype.onAnnotationResults = function (message, responseCallback) {
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new session_protocol_1.Response());
        // Notify annotation activation callbacks
        for (var _i = 0, _a = message.ops; _i < _a.length; _i++) {
            var op = _a[_i];
            this.callAnnotationCallbacks(op, message.annotationType);
            if (this.hostCallbacks.onAnnotationResult) {
                this.hostCallbacks.onAnnotationResult(op);
            }
        }
    };
    Session.prototype.onAnnotationResultStateMessage = function (message, responseCallback) {
        var _a;
        // Acknowledge receiving the annotation before invoking any of the callbacks to get an accurate
        // report on the AL operations on when the annotation was received by the client
        responseCallback(undefined, new session_protocol_1.Response());
        for (var _i = 0, _b = message.updates; _i < _b.length; _i++) {
            var update = _b[_i];
            var annotationType = update.annotationType, state = update.state;
            if (!this.tokensByAnnotationType.has(annotationType)) {
                continue;
            }
            // Get or determine old state if it's not available.
            // Updates are only sent if state has changed, so we can
            // determind the old state if it's not previously known.
            var oldState = void 0;
            if (this.annotationResultStates.has(annotationType)) {
                oldState = this.annotationResultStates.get(annotationType);
            }
            else {
                if (state === session_protocol_1.AnnotationResultState.Idle) {
                    oldState = session_protocol_1.AnnotationResultState.Pending;
                }
                else {
                    oldState = session_protocol_1.AnnotationResultState.Idle;
                }
            }
            for (var _c = 0, _d = this.tokensByAnnotationType.get(annotationType); _c < _d.length; _c++) {
                var token = _d[_c];
                var tracker = this.annotationActivationTrackers.get(token);
                if ((_a = tracker.options) === null || _a === void 0 ? void 0 : _a.stateUpdateCallback) {
                    tracker.options.stateUpdateCallback(oldState, state);
                }
            }
        }
    };
    Session.prototype.resetNextSyncSequenceId = function () {
        this.nextSyncSequenceId = 1;
    };
    Session.prototype.onReconnect = function () {
        this.isSessionConnected = true;
        this.annotationActivationTrackers.forEach(function (tracker) {
            tracker.activate(true /*ignoreExistingAnnotations*/);
        });
        if (this.reconnectCallback) {
            this.reconnectCallback();
        }
    };
    Session.prototype.onSessionClose = function (sessionCloseMessage) {
        this.isSessionClosed = true;
        this.isSessionConnected = false;
        if (this.sessionCloseCallback) {
            this.sessionCloseCallback(sessionCloseMessage);
        }
    };
    Session.prototype.onConnect = function (isSeedingRequired, sessionUrl, origin, authToken) {
        if (this.hasSessionConnected) {
            // If seeding is required on reconnect reset allowSeed flags, seedGroupSize counter, and seedBatchedOperationsManager.
            // Otherwise we will continue with already started seeding if reconnect occurred during the seeding
            if (isSeedingRequired) {
                this.allowSeed = true;
                this.allowGroupSeed = true;
                this.seedGroupSize = 0;
                if (this.seedBatchedOperationsManager) {
                    this.seedBatchedOperationsManager.removeAllBatchedItems();
                    this.seedBatchedOperationsManager = undefined;
                }
                if (this.batchedOperationsManager) {
                    this.batchedOperationsManager.removeAllBatchedItems();
                    this.batchedOperationsManager = undefined;
                }
            }
        }
        this.isSessionConnected = true;
        this.hasSessionConnected = true;
        if (this.connectCallback) {
            this.connectCallback(isSeedingRequired, sessionUrl, origin, authToken);
        }
    };
    Session.prototype.onDisconnect = function (error) {
        this.isSessionConnected = false;
        if (this.disconnectCallback) {
            this.disconnectCallback(error);
        }
    };
    Session.prototype.getSessionManager = function () {
        if (!this.sessionManager) {
            this.sessionManager = new empty_session_manager_1.EmptySessionManager();
        }
        return this.sessionManager;
    };
    Session.prototype.callAnnotationCallbacks = function (op, annotationType) {
        if (this.annotationCallbacks.size === 0) {
            return;
        }
        var callbackMap = this.annotationCallbacks.get(annotationType);
        if (callbackMap) {
            callbackMap.forEach(function (callback) {
                callback(op);
            });
        }
    };
    Session.prototype.getOperationBatchConfig = function (batchOptions) {
        // Group all arrays of operations together
        var groupingKeyExtractor = function () {
            return 'operations';
        };
        // Combine operations into a single array
        var multiplex = function (operations) {
            return {
                input: operations.filter(function (ops) { return ops.length; }).reduce(function (ops, array) { return ops.concat(array); }, []),
                demultiplex: function () { return [[]]; } // NOTE: Unused because no callback is specified to addBatchItem
            };
        };
        // Split large operations arrays into smaller arrays
        var split = function (operations) {
            // Cannot split array with 1 or fewer operations
            if (operations.length <= 1) {
                return undefined;
            }
            return {
                inputs: operations.reduce(function (ops, op) {
                    ops.push([op]);
                    return ops;
                }, []),
                join: function () { return []; } // NOTE: Unused because no callback is specified to addBatchItem
            };
        };
        return {
            delayMs: batchOptions.delayMs,
            maxInputSize: batchOptions.maxInputSize,
            estimateSize: estimate_size_1.estimateSize,
            groupingKeyExtractor: groupingKeyExtractor,
            multiplex: multiplex,
            split: split
        };
    };
    Session.prototype.sendSeedMessagesViaBatchManager = function (operations, groupComplete, cv) {
        var _this = this;
        if (!this.seedBatchedOperationsManager) {
            // Submit will be invoked once per batch of operations
            var submit = function (operations, batch, callback) {
                // Update the count of batches
                _this.seedGroupSize++;
                // Send the SyncMessage for the operations in this batch
                _this.getSessionManager().sendMessage(new session_protocol_1.SyncMessage({
                    cv: batch.cv,
                    seq: 0,
                    ops: operations,
                    groupId: 'Seed',
                    groupSize: batch.groupComplete ? _this.seedGroupSize : undefined,
                    groupComplete: batch.groupComplete ? batch.groupComplete : undefined
                }), function (errorResponse) {
                    callback(errorResponse ? new Error(errorResponse.error) : undefined);
                });
            };
            // Instantiate the seed batch manager
            this.seedBatchedOperationsManager = new batch_manager_1.BatchManager(submit);
        }
        // Instantiate the seed batch manager and add the seed operations to it
        this.seedBatchedOperationsManager.addBatchItem(operations, this.operationBatchConfig, { name: 'SubmitSeedOperations' }, cv, groupComplete);
    };
    return Session;
}());
exports.Session = Session;
//# sourceMappingURL=session.js.map